import { useEffect } from "react";

const MAX_BORDER_RADIUS_PX = 30;
const CORNER_PROPERTIES = [
  "border-top-left-radius",
  "border-top-right-radius",
  "border-bottom-right-radius",
  "border-bottom-left-radius",
];

const shouldSkipRadius = (radiusValue) => !radiusValue || radiusValue.includes("%");

const clampRadiusValue = (radiusValue) => {
  if (shouldSkipRadius(radiusValue) || !radiusValue.endsWith("px")) {
    return null;
  }

  const radius = Number.parseFloat(radiusValue);
  if (!Number.isFinite(radius) || radius <= MAX_BORDER_RADIUS_PX) {
    return null;
  }

  return `${MAX_BORDER_RADIUS_PX}px`;
};

const capBorderRadius = (element) => {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  const computedStyle = window.getComputedStyle(element);

  CORNER_PROPERTIES.forEach((propertyName) => {
    const radiusValue = computedStyle.getPropertyValue(propertyName).trim();
    const clampedValue = clampRadiusValue(radiusValue);

    if (clampedValue) {
      element.style.setProperty(propertyName, clampedValue, "important");
    }
  });
};

const capBorderRadiusInTree = (rootNode) => {
  if (!(rootNode instanceof HTMLElement)) {
    return;
  }

  capBorderRadius(rootNode);
  rootNode.querySelectorAll("*").forEach(capBorderRadius);
};

function BorderRadiusCap() {
  useEffect(() => {
    if (typeof window === "undefined" || !document.body) {
      return undefined;
    }

    let frameId = null;
    const pendingRoots = new Set();

    const flushPendingRoots = () => {
      frameId = null;
      pendingRoots.forEach((rootNode) => capBorderRadiusInTree(rootNode));
      pendingRoots.clear();
    };

    const queueRoot = (rootNode) => {
      pendingRoots.add(rootNode instanceof HTMLElement ? rootNode : document.body);

      if (frameId === null) {
        frameId = window.requestAnimationFrame(flushPendingRoots);
      }
    };

    queueRoot(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              queueRoot(node);
            }
          });
          return;
        }

        if (mutation.target instanceof HTMLElement) {
          queueRoot(mutation.target);
        }
      });
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    return () => {
      observer.disconnect();
      pendingRoots.clear();
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  return null;
}

export default BorderRadiusCap;
